import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { IMAGES, PHONE, PHONE_HREF, LOGO, TAGLINE_IMG } from "../lib/content";
import { scrollToId } from "../hooks/useSmoothScroll";

export const Hero = ({ onConsult }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen w-full overflow-hidden">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.heroBg}
          alt=""
          className="h-full w-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#1a1410]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.14),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 pt-32 sm:pt-36 pb-24 min-h-screen flex flex-col justify-center"
      >
        <motion.img
          src={LOGO}
          alt="B. Thornton Capital & Asset Group"
          initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="w-[280px] sm:w-[420px] lg:w-[500px] h-auto mb-4 drop-shadow-[0_0_40px_rgba(212,175,55,0.25)]"
        />
        <motion.img
          src={TAGLINE_IMG}
          alt="Recovering what's rightfully yours"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-[230px] sm:w-[320px] lg:w-[380px] h-auto mb-8 drop-shadow-[0_0_18px_rgba(212,175,55,0.2)]"
        />

        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-[11px] sm:text-xs font-sans tracking-[0.32em] uppercase text-amber-500 mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-amber-500/60" />
          Asset Recovery · Real Estate · Credit · Business Launch · Web &amp; Brand
        </motion.span>

        <h1 className="font-serif font-black leading-[0.95] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
          <MaskedLines
            delay={0.55}
            lines={["Building Wealth.", "Restoring Opportunity."]}
          />
          <MaskedLines
            delay={0.83}
            lines={["Creating Legacy."]}
            lineClassName="text-gold-foil"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-zinc-300"
        >
          Your complete financial &amp; business solutions partner. Whether you're
          recovering surplus funds, building credit, investing in real estate, or
          launching a new business — we provide the expertise, guidance, and
          strategy to help you achieve long-term financial success.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            onClick={onConsult}
            data-testid="hero-consult-btn"
            className="group bg-gold-foil text-black text-[13px] font-bold tracking-[0.14em] uppercase px-8 py-4 hover:brightness-110 transition-all duration-300"
          >
            Schedule Your Free 15-Minute Consultation
          </button>
          <a
            href={PHONE_HREF}
            data-testid="hero-phone-btn"
            className="group flex items-center gap-3 border border-amber-500/40 text-amber-300 text-[13px] font-bold tracking-[0.14em] uppercase px-8 py-4 hover:bg-amber-500 hover:text-black transition-colors duration-300"
          >
            <Phone className="h-4 w-4" /> Call {PHONE}
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-amber-500/70 hover:text-amber-400"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="block"
        >
          <ArrowDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
};
